/**
 * Normalizes dashboard scalar API payloads so React Query never receives `undefined`.
 * Handles V3 `{ value }` envelopes, legacy dev-mock bare numbers, and empty catch-alls.
 */

export type DashboardPropertyStatisticsShape = {
  baseSqft: number;
  rentableSqft: number;
  grossSqft: number;
  totalFloors: number;
  opportunitySqft: number;
  opportunityValue: number;
  opportunityPercent: number;
};

/** Zeroed property statistics used when the API returns a non-object (e.g. dev-mock `[]`). */
export const EMPTY_PROPERTY_STATISTICS: DashboardPropertyStatisticsShape = {
  baseSqft: 0,
  rentableSqft: 0,
  grossSqft: 0,
  totalFloors: 0,
  opportunitySqft: 0,
  opportunityValue: 0,
  opportunityPercent: 0,
};

/**
 * Reads a numeric scalar from a dashboard endpoint response.
 * Returns `null` when the value is absent or not a finite number — never `undefined`.
 */
export function readDashboardScalar(payload: unknown): number | null {
  if (payload == null) return null;
  if (typeof payload === 'number' && Number.isFinite(payload)) return payload;
  if (typeof payload === 'object' && !Array.isArray(payload) && 'value' in payload) {
    const value = (payload as { value: unknown }).value;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
  return null;
}

/**
 * Reads a count field from `{ count: number }` (or a bare number in dev mocks).
 * Returns `null` when absent — never `undefined`.
 */
export function readDashboardCount(payload: unknown): number | null {
  if (payload == null) return null;
  if (typeof payload === 'number' && Number.isFinite(payload)) return payload;
  if (typeof payload === 'object' && !Array.isArray(payload) && 'count' in payload) {
    const count = (payload as { count: unknown }).count;
    return typeof count === 'number' && Number.isFinite(count) ? count : null;
  }
  return null;
}

/** Coerces a property statistics object; falls back to zeros when the payload is not a stats block. */
export function readPropertyStatistics(payload: unknown): DashboardPropertyStatisticsShape {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ...EMPTY_PROPERTY_STATISTICS };
  }
  const r = payload as Record<string, unknown>;
  const num = (key: string): number => {
    const v = r[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  };
  return {
    baseSqft: num('baseSqft'),
    rentableSqft: num('rentableSqft'),
    grossSqft: num('grossSqft'),
    totalFloors: num('totalFloors'),
    opportunitySqft: num('opportunitySqft'),
    opportunityValue: num('opportunityValue'),
    opportunityPercent: num('opportunityPercent'),
  };
}
